import { Button } from "@/shared";
import MainLayout from "@/shared/components/MainLayout";
import DatePicker from "@/shared/components/ui/DatePicker";
import { Dropdown, DropdownItem } from "@/shared/components/ui/Dropdown";
import Header from "@/shared/components/ui/Header";
import { motion } from 'framer-motion';
import { ListOrdered, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import ItemFilterDialog from "./ItemFilterDialog";
import ItemSalesReport from "./ItemSalesReport";
import useItemSales from "@/hooks/ItemSales/useItemSales";
import { useNavigate } from "react-router-dom";

const ItemSales = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { handleQuickSelect,
        displayReport,
        setDisplayReport,
        groupedData,
        loading,
        q,
        date,
        setDate,
        dialog,
        setDialog,
        itemSales,
        onCloseDialog } = useItemSales();
    return (
        <MainLayout >
            <Header title={`${t('item sales')}`} info={q} />

            <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-center">
                <div className="w-full max-w-6xl">
                    <div className="mb-6 w-full max-w-6xl">
                        <div className="flex flex-col justify-between xl:flex-row xl:items-end items-stretch gap-4 w-full">
                            <div className="grid grid-cols-2 gap-2 md:gap-4">
                                <div className="flex flex-col gap-1 min-w-0">
                                    <Dropdown label={t("start_date")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today', 'from')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday', 'from')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month', 'from')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month', 'from')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                    <DatePicker
                                        id="date-picker"
                                        label=''
                                        placeholder={t("Select a date")}
                                        onChange={(_, currentDateString) => setDate((prev) => ({ ...prev, from: currentDateString }))}
                                        defaultDate={date.from}
                                    />
                                </div>
                                <div className="flex flex-col gap-1 min-w-0">
                                    <Dropdown label={t("end_date")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today', 'to')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday', 'to')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month', 'to')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month', 'to')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                    <DatePicker
                                        id="date-picker1"
                                        label=""
                                        placeholder={t("Select a date")}
                                        onChange={(_, currentDateString) => setDate((prev) => ({ ...prev, to: currentDateString }))}
                                        defaultDate={date.to}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 xl:flex gap-2 xl:items-end">
                                <Button
                                    className="w-full xl:w-auto text-[12px] px-2"
                                    onClick={() => setDialog((prev) => ({ ...prev, open: true }))}
                                >
                                    <span className="truncate">{t("view_report")}</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <Loader />
                        </div>
                    ) : (
                        <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
                            <div className="overflow-x-auto">
                                {displayReport === 'Grouped Layout' ? (<ItemSalesReport data={groupedData()} />) : (
                                    <table className="w-full text-right border-collapse">
                                        <thead>
                                            <tr className="text-slate-400 uppercase text-[11px] font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                                <th className="px-6 py-4">{t("accountid")}</th>
                                                <th className="px-6 py-4">{t("accountname")}</th>
                                                <th className="px-6 py-4">{t("invoice_date")}</th>
                                                <th className="px-6 py-4">{t("invoice_id")}</th>
                                                <th className="px-6 py-4">{t("invoice_type")}</th>
                                                <th className="px-6 py-4">{t("item_id")}</th>
                                                <th className="px-6 py-4">{t("item_name")}</th>
                                                <th className="px-6 py-4">{t("item_quantity")}</th>
                                                <th className="px-6 py-4">{t("item_price")}</th>
                                                <th className="px-6 py-4">{t("item_total")}</th>

                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {itemSales.length > 0 ? (
                                                itemSales.map((item: any, index: number) => {
                                                    const isInitialBalance = item?.invoice_type?.toLowerCase() === "old";
                                                    return (
                                                        <motion.tr
                                                            key={index}
                                                            initial={{ opacity: 0, y: 10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: index * 0.03 }}
                                                            className={`hover:bg-violet-50/30 transition-colors group`}
                                                        >
                                                            <td className="px-6 py-4 text-sm font-medium text-slate-700">
                                                                {item?.account_id || "---"}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {item?.account_name || "---"}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {item?.invoice_date || "---"}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm">
                                                                <span
                                                                    onClick={() => {
                                                                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                                                        navigate(`/invoice_details/${item?.invoice_id}`)
                                                                    }}
                                                                    className="bg-slate-100 text-slate-700 px-2 py-1 rounded shadow-sm border border-slate-200 cursor-pointer">
                                                                    {item?.invoice_id}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm">
                                                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${isInitialBalance ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                                                                    {item?.invoice_type || "N/A"}
                                                                </span>
                                                            </td>

                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {item?.item_id || "---"}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {item?.item_name || "---"}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {item?.item_quantity || "---"}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {Number(item?.item_price || 0).toFixed(2)}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                                {Number(Number(item?.item_price || 0) * Number(item?.item_quantity || 0)).toFixed(2)}
                                                            </td>
                                                        </motion.tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} className="px-6 py-20 text-center">
                                                        <div className="flex flex-col items-center gap-3 opacity-20">
                                                            <ListOrdered size={48} />
                                                            <p className="font-black uppercase tracking-widest text-sm">{t("No Data Found")}</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}

                            </div>
                        </div>
                    )}
                </div>
            </main>


            <ItemFilterDialog
                dialog={dialog}
                onCloseDialog={onCloseDialog}
                setDisplayReport={setDisplayReport}
                setDialog={setDialog} />

        </MainLayout>
    )
}

export default ItemSales;